import React, { useContext } from "react";
import { AdvTaskOverallStatusType } from "../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskOverallStatusType";
import AdvTaskListingDto from "../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import AdvTaskListingUserDetailDto from "../../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingUserDetailDto";
import { SelectedStatus } from "./AdvTaskStatusMultiSelect";
import cx from "classnames";
import { t } from "i18next";
import UserContext from "../../../state/UserContext";
import { ClientTaskType } from "../../../types/dtos/tasks/advanced-tasks/ClientTaskType";

interface StatisticsBarProps {
  tasks: AdvTaskListingDto[] | null;
  originalTasks: AdvTaskListingDto[] | null;
  taskType: ClientTaskType | null;
  selectedStatuses: SelectedStatus[];
  searchTerm: string | null;
  onStatusChange: (statuses: SelectedStatus[]) => void;
  originalUsers: AdvTaskListingUserDetailDto[] | null;
}

const AdvTaskListingStatisticsBar: React.FC<StatisticsBarProps> = ({
  tasks,
  originalTasks,
  taskType,
  selectedStatuses,
  searchTerm,
  onStatusChange,
  originalUsers,
}) => {
  const userContext = useContext(UserContext);

  let taskTypeName = t("Timeline.Filters.Tasks");
  if (taskType && taskType.pluralNameTranslationKeyIdentifier) {
    taskTypeName = t(taskType.pluralNameTranslationKeyIdentifier);
  }

  // Calculate counts from the filtered tasks
  const calculateStatusCounts = (
    taskList: AdvTaskListingDto[] | null,
  ): Record<string, number> => {
    if (!taskList) return {};

    return taskList.reduce((acc: Record<string, number>, task) => {
      const { overallStatus } = task;
      if (overallStatus !== "UNKNOWN") {
        // For completed tasks, apply date range filtering if needed
        if (overallStatus === "COMPLETED") {
          const completedStatus = selectedStatuses.find(
            (s) => s.key === "COMPLETED",
          );
          if (completedStatus?.dateRange === "12months") {
            // Filter to last 12 months
            const twelveMonthsAgo = new Date();
            twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
            const taskDate = new Date(task.dateCompleted || task.targetDate);
            if (taskDate < twelveMonthsAgo) {
              return acc; // Skip this task
            }
          }
        }
        acc[overallStatus] = (acc[overallStatus] || 0) + 1;
      }
      return acc;
    }, {});
  };

  // Get counts for filtered and original tasks
  const filteredCounts = calculateStatusCounts(tasks);
  const totalCounts = calculateStatusCounts(originalTasks);

  // Add helper function to determine filter type
  const getFilterType = () => {
    const isStatusFiltered =
      selectedStatuses.length < 5 && selectedStatuses.length > 0; // Less than all 5 statuses selected but at least one
    const isSearchFiltered = searchTerm !== null && searchTerm.trim() !== "";

    if (isStatusFiltered && isSearchFiltered) {
      return "both";
    } else if (isStatusFiltered) {
      return "status";
    } else if (isSearchFiltered) {
      return "search";
    }
    return "none";
  };

  // Determine if we're filtering (so we show both numbers)
  const filterType = getFilterType();
  const isFiltering = filterType !== "none";

  const getLabel = (status: AdvTaskOverallStatusType): string => {
    switch (status) {
      case "ACTIVE":
        return `Active`;
      case "COMPLETED":
        return `Completed`;
      case "OVERDUE":
        return `Overdue`;
      default:
        return status;
    }
  };

  const getSubtitle = (status: AdvTaskOverallStatusType): string | null => {
    if (status === "COMPLETED") {
      const completedStatus = selectedStatuses.find(
        (s) => s.key === "COMPLETED",
      );
      if (completedStatus?.dateRange === "12months") {
        return "In the last 12 months";
      } else if (completedStatus?.dateRange === "alltime") {
        return "All time";
      }
    }
    return null;
  };

  // Only show Active, Completed, and Overdue cards
  const orderedStatuses: AdvTaskOverallStatusType[] = [
    "ACTIVE",
    "OVERDUE",
    "COMPLETED",
  ];

  // Handle status card click to toggle filtering
  const handleStatusClick = (status: AdvTaskOverallStatusType) => {
    const isCurrentlySelected = selectedStatuses.some((s) => s.key === status);

    if (isCurrentlySelected) {
      // Remove this status from selection
      const newSelection = selectedStatuses.filter((s) => s.key !== status);
      onStatusChange(newSelection);
    } else {
      // Add this status to selection with default date range if applicable
      const newStatus: SelectedStatus = {
        key: status,
        dateRange: status === "COMPLETED" ? "alltime" : undefined,
      };
      onStatusChange([...selectedStatuses, newStatus]);
    }
  };

  return (
    <div>
      <div className="statistics-bar grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 py-3 sm:py-4">
        {orderedStatuses.map((status) => {
          const isSelected = selectedStatuses.some((s) => s.key === status);
          const filteredCount = filteredCounts[status] || 0;
          const totalCount = totalCounts[status] || 0;

          return (
            <div
              key={status}
              onClick={() => handleStatusClick(status)}
              className={cx(
                "stat-item bg-white p-2 sm:p-4 rounded-lg shadow-sm border flex justify-between items-center relative cursor-pointer transition-all",
                isSelected
                  ? "primary-text font-semibold border primary-border"
                  : "border-gray-200 text-gray-700 hover:shadow-md hover:border-gray-300",
              )}
              style={
                isSelected
                  ? {
                      backgroundColor: `#${userContext?.user?.client.primaryHexColour}10`,
                    }
                  : {}
              }
            >
              <div
                className={cx(
                  "absolute left-0 top-0 bottom-0 w-1 rounded-tl-lg rounded-bl-lg primary-bg",
                )}
              ></div>
              <div className="flex flex-col items-start pl-2">
                <p className="font-medium text-sm sm:text-lg">
                  {getLabel(status)}
                </p>
                {getSubtitle(status) && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {getSubtitle(status)}
                  </p>
                )}
              </div>
              <div className="text-right">
                {isFiltering ? (
                  // Show both filtered and total counts when filtering
                  <div>
                    <p className="text-base sm:text-xl font-semibold flex items-center justify-end">
                      <span
                        className={`${
                          isSelected ? "primary-text font-bold" : "primary-text"
                        } mr-1 text-xs sm:text-base`}
                      >
                        {filteredCount}
                      </span>
                      <span
                        className={`${
                          isSelected ? "primary-text" : "primary-text"
                        } mr-1 text-xs sm:text-sm`}
                      >
                        /
                      </span>
                      <span className={isSelected ? "primary-text" : ""}>
                        {totalCount}
                      </span>
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {taskTypeName}
                    </p>
                  </div>
                ) : (
                  // Show only one number when not filtering
                  <div>
                    <p className="text-base sm:text-xl font-semibold">
                      {totalCount}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {taskTypeName}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdvTaskListingStatisticsBar;
